// src/components/Dashboard/EFIpieChart.js

import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, CircularProgress, Alert } from '@mui/material';
import PropTypes from 'prop-types';

// ... [Keep the Custom Pie Animation code as is] ...

const EFIpieChart = ({ day, method = 'Aggregated', efiData }) => {
  // If efiData is not provided or loading
  if (!efiData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { efiResultsAllDays, efiDescriptions, hoursStatsAllDays } = efiData;

  // Check if we have valid data for the selected day
  if (!efiResultsAllDays || !hoursStatsAllDays || !efiDescriptions ||
    !efiResultsAllDays[day - 1] || !hoursStatsAllDays[day - 1]) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">No data available for Day {day}</Alert>
      </Box>
    );
  }

  // Extract data for the selected day
  const selectedDayEfiData = efiResultsAllDays[day - 1];
  const selectedDayHoursData = hoursStatsAllDays[day - 1];

  // Define the four specific flexibility categories
  const desiredCategories = ['peak_peak_hours', 'off_peak_peak_hours', 'peak_off_peak_hours', 'off_off_hours'];

  const categoryColors = {
    peak_peak: '#947fff',           // Purple for EFI UP
    off_peak_peak: '#fb5801',       // Orange for EFI UP
    peak_off_peak: '#ba3da0',       // Pinkish for EFI UP
    off_off: '#5581fc',             // Blue for EFI UP
  };

  // Calculate the number of hours per desired category
  const categoryCounts = {
    peak_peak: 0,
    off_peak_peak: 0,
    peak_off_peak: 0,
    off_off: 0
  };

  desiredCategories.forEach((categoryKey) => {
    const category = categoryKey.replace('_hours', '').toLowerCase(); // e.g., 'peak_peak'
    const hours = selectedDayHoursData[categoryKey]; // array of hours for this category

    if (hours && Array.isArray(hours)) {
      categoryCounts[category] += hours.length;
    }
  });

  // Calculate total hours to ensure it's 24
  const totalHours = Object.values(categoryCounts).reduce((sum, count) => sum + count, 0);

  // Handle cases where totalHours != 24
  if (totalHours !== 24) {
    console.warn(`Total hours (${totalHours}) do not sum up to 24.`);
    // Optionally, you can adjust or normalize the counts here
  }

  // Prepare data for pie chart
  const pieData = Object.keys(categoryCounts).map((category) => ({
    name: category
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
    y: (categoryCounts[category] / 24) * 100, // Percentage
    color: categoryColors[category] || 'gray'
  }));

  // Define Highcharts options
  const options = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
    },
    title: {
      text: null,
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
    },
    accessibility: {
      enabled: false
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        borderWidth: 2,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b><br>{point.percentage:.1f}%',
          distance: 20,
        },
      },
    },
    series: [
      {
        name: 'Categories',
        colorByPoint: true,
        data: pieData,
        enableMouseTracking: false, // Disable initially for custom animation
        animation: {
          duration: 2000,
        },
      },
    ],
    credits: {
      enabled: false,
    },
  };

  // Custom Legend
  // const renderLegend = () => {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
  //       {pieData.map((point) => (
  //         <Box key={point.name} sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
  //           <Box
  //             sx={{
  //               width: 16,
  //               height: 16,
  //               backgroundColor: point.color,
  //               mr: 1,
  //             }}
  //           />
  //           <Typography variant="body2">{point.name}</Typography>
  //         </Box>
  //       ))}
  //     </Box>
  //   );
  // };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <Box sx={{ flexGrow: 1, height: '80%', width: '100%' }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Box>
      {/* <Box sx={{ mt: 2, height: '20%' }}>
        {renderLegend()}
      </Box> */}
    </Box>
  );
};

EFIpieChart.propTypes = {
  day: PropTypes.number.isRequired,
  method: PropTypes.string,
  efiData: PropTypes.shape({
    efiResultsAllDays: PropTypes.array,
    efiDescriptions: PropTypes.object,
    hoursStatsAllDays: PropTypes.array,
  }),
};

export default EFIpieChart;
